<template>
    <div class="min-h-screen flex items-center justify-center relative overflow-hidden py-8 px-4"
        style="background:#faf8f5; font-family:'Sora',sans-serif;">

        <!-- ── Ambient background ───────────────────────────── -->
        <div class="fixed inset-0 pointer-events-none z-0">
            <div class="absolute rounded-full opacity-40"
                style="width:520px;height:520px;top:-120px;right:-100px;background:radial-gradient(circle,#fdba74,transparent 70%);filter:blur(80px);animation:orb-drift 14s ease-in-out infinite alternate;"></div>
            <div class="absolute rounded-full opacity-35"
                style="width:380px;height:380px;bottom:-80px;left:-60px;background:radial-gradient(circle,#fde68a,transparent 70%);filter:blur(80px);animation:orb-drift 18s ease-in-out infinite alternate-reverse;"></div>
            <div class="absolute inset-0"
                style="background-image:linear-gradient(rgba(0,0,0,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.03) 1px,transparent 1px);background-size:40px 40px;"></div>
        </div>

        <!-- ── Modal signature ──────────────────────────────── -->
        <modal name="modal-signature-bienvenue" width="860px" height="auto" :scrollable="true">
            <div class="p-8" style="font-family:'Sora',sans-serif;">
                <div class="flex justify-between items-center mb-6 pb-4 border-b border-stone-200">
                    <div class="flex items-center gap-2 text-lg font-bold text-stone-800">
                        <span class="text-orange-500">✦</span>
                        Signature de l'entreprise
                    </div>
                    <button @click="$modal.hide('modal-signature-bienvenue')"
                        class="text-stone-400 hover:text-red-500 transition-colors text-xl bg-transparent border-none cursor-pointer">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <div class="flex gap-1 bg-stone-50 border border-stone-200 rounded-xl p-1 mb-6">
                    <button class="flex-1 text-center py-2.5 px-3 rounded-lg text-sm font-medium transition-all border-none cursor-pointer"
                        :class="selectedSignatureMethod === 'electronique'
                            ? 'bg-white text-orange-600 font-bold shadow-sm'
                            : 'bg-transparent text-stone-500 hover:text-stone-700'"
                        @click="selectedSignatureMethod = 'electronique'">
                        <i class="fas fa-pen-nib mr-2"></i> Signature électronique
                    </button>
                    <button class="flex-1 text-center py-2.5 px-3 rounded-lg text-sm font-medium transition-all border-none cursor-pointer"
                        :class="selectedSignatureMethod === 'fichier'
                            ? 'bg-white text-orange-600 font-bold shadow-sm'
                            : 'bg-transparent text-stone-500 hover:text-stone-700'"
                        @click="selectedSignatureMethod = 'fichier'">
                        <i class="fas fa-image mr-2"></i> Importer une image
                    </button>
                </div>

                <div class="min-h-[200px]">
                    <template v-if="selectedSignatureMethod === 'electronique'">
                        <input-signature v-model="form.signature_entreprise"
                            @input="$modal.hide('modal-signature-bienvenue')" />
                    </template>
                    <template v-else>
                        <div class="border-2 border-dashed border-stone-200 rounded-2xl p-12 text-center hover:border-orange-400 hover:bg-orange-50/30 transition-all cursor-pointer">
                            <div class="text-4xl text-orange-400 mb-3"><i class="fas fa-cloud-upload-alt"></i></div>
                            <p class="font-semibold text-stone-700 text-sm mb-1">Glissez votre fichier ici</p>
                            <p class="text-xs text-stone-400">PNG ou JPG · Fond transparent recommandé</p>
                            <input-file key-name="signature-bienvenue" v-model="form.signature_entreprise"
                                accept="image/png, image/jpeg"
                                @input="$modal.hide('modal-signature-bienvenue')" />
                        </div>
                    </template>
                </div>
            </div>
        </modal>

        <!-- ── Steps ────────────────────────────────────────── -->
        <transition name="tf-slide" mode="out-in">

            <!-- STEP 0 : Accueil -->
            <div v-if="step === 0" key="s0"
                class="relative z-10 bg-white border border-stone-200 rounded-3xl w-full max-w-lg p-10"
                style="box-shadow:0 4px 6px -1px rgba(0,0,0,.06),0 20px 60px -10px rgba(234,88,12,.10);">

                <span class="inline-flex items-center text-[11px] font-semibold tracking-widest uppercase text-orange-600 bg-orange-50 border border-orange-200 rounded-full px-3 py-1 mb-7">
                    Configuration initiale
                </span>

                <h1 class="text-[28px] leading-snug text-stone-800 mb-3" style="font-family:'DM Serif Display',serif;letter-spacing:-0.5px;">
                    Bienvenue, {{ $account.account.prenom }}.
                </h1>
                <p class="text-sm text-stone-500 leading-relaxed mb-6">
                    Quelques informations manquent encore à votre dossier pour que vous puissiez pleinement profiter de Termoflow
                </p>

                <div class="bg-stone-50 border border-stone-200 rounded-xl p-4 flex flex-col gap-2.5 mb-8">
                    <div v-for="item in missingFields" :key="item.key"
                        class="flex items-center gap-2.5 text-sm text-stone-700">
                        <span class="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"></span>
                        {{ item.label }}
                    </div>
                    <div v-if="missingFields.length === 0" class="flex items-center gap-2 text-sm text-stone-500">
                        <i class="fas fa-check-circle text-emerald-500"></i>
                        Tout est déjà configuré !
                    </div>
                </div>

                <button @click="step = 1"
                    class="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 hover:-translate-y-0.5 border-none cursor-pointer text-base"
                    style="box-shadow:0 4px 15px rgba(234,88,12,.3);">
                    Compléter mon profil <i class="fas fa-arrow-right"></i>
                </button>

                <button @click="logout"
                    class="w-full text-center text-xs text-stone-400 hover:text-stone-500 mt-4 py-1 bg-transparent border-none cursor-pointer transition-colors block">
                    Se déconnecter
                </button>
            </div>

            <!-- STEP 1 : Formulaire -->
            <div v-else-if="step === 1" key="s1"
                class="relative z-10 bg-white border border-stone-200 rounded-3xl w-full max-w-xl overflow-hidden"
                style="box-shadow:0 4px 6px -1px rgba(0,0,0,.06),0 20px 60px -10px rgba(234,88,12,.10);">

                <div class="h-1 bg-stone-100">
                    <div class="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500" style="width:70%;border-radius:0 2px 2px 0;"></div>
                </div>

                <div class="p-10">
                    <div class="mb-7">
                        <div class="text-[11px] font-semibold tracking-widest uppercase text-orange-500 mb-2">Étape 1 sur 1</div>
                        <h2 class="text-[22px] text-stone-800 mb-1" style="font-family:'DM Serif Display',serif;letter-spacing:-0.5px;">
                            Finalisez votre société
                        </h2>
                        <p class="text-sm text-stone-500 leading-relaxed">
                            Ces informations apparaîtront sur vos contrats, plannings et documents officiels.
                        </p>
                    </div>

                    <div class="flex flex-col gap-6">

                        <div v-if="!$account.account.societe.num_capacite">
                            <label class="flex items-center gap-2 text-sm font-semibold text-stone-700 mb-1">
                                Numéro de capacité
                                <span class="text-[10px] font-semibold uppercase tracking-wider text-orange-500 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">CERFA</span>
                            </label>
                            <p class="text-xs text-stone-400 mb-2 leading-relaxed">Délivré par la préfecture, il autorise l'exercice de votre activité.</p>
                            <input v-model="form.num_capacite" type="text" placeholder="Ex : 2024-75-0001234"
                                class="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-stone-800 bg-stone-50 outline-none transition-all focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-500/10 placeholder:text-stone-300"
                                style="border-width:1.5px;" />
                        </div>

                        <div v-if="!$account.account.societe.responsable_rc">
                            <label class="flex items-center gap-2 text-sm font-semibold text-stone-700 mb-1">
                                Responsabilité Civile
                                <span class="text-[10px] font-semibold uppercase tracking-wider text-orange-500 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">RC</span>
                            </label>
                            <p class="text-xs text-stone-400 mb-3 leading-relaxed">
                                Ces informations apparaîtront sur vos documents officiels.
                                Exemple de résultat : <span class="font-mono text-stone-500">MAAF - Contrat n°101053256M - France Métropolitaine</span>
                            </p>

                            <div class="flex flex-col gap-2">
                                <!-- Organisme -->
                                <div>
                                    <label class="text-xs text-stone-500 mb-1 block">Organisme assureur <span class="text-orange-500">*</span></label>
                                    <input v-model="form.rc_organisme" type="text"
                                        placeholder="Ex : MAAF, AXA, MCE001…"
                                        class="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-stone-800 bg-stone-50 outline-none transition-all focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-500/10 placeholder:text-stone-300"
                                        style="border-width:1.5px;" />
                                </div>

                                <!-- Numéro de contrat -->
                                <div>
                                    <label class="text-xs text-stone-500 mb-1 block">Numéro de contrat <span class="text-orange-500">*</span></label>
                                    <input v-model="form.rc_contrat" type="text"
                                        placeholder="Ex : 101053256M"
                                        class="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-stone-800 bg-stone-50 outline-none transition-all focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-500/10 placeholder:text-stone-300 font-mono"
                                        style="border-width:1.5px;" />
                                </div>

                                <!-- Zone géographique (optionnel) -->
                                <div>
                                    <label class="text-xs text-stone-500 mb-1 block">
                                        Zone couverte
                                        <span class="text-stone-300 font-normal ml-1">optionnel</span>
                                    </label>
                                    <input v-model="form.rc_zone" type="text"
                                        placeholder="Ex : France Métropolitaine"
                                        class="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-stone-800 bg-stone-50 outline-none transition-all focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-500/10 placeholder:text-stone-300"
                                        style="border-width:1.5px;" />
                                </div>

                                <!-- Aperçu de la string reconstruite -->
                                <transition name="tf-slide">
                                    <div v-if="form.rc_organisme || form.rc_contrat"
                                        class="text-xs text-stone-400 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 font-mono leading-relaxed">
                                        <span class="text-stone-300 not-italic font-sans">Résultat → </span>
                                        <span class="text-stone-600">
                                            {{ [
                                                form.rc_organisme,
                                                form.rc_contrat ? `Contrat n°${form.rc_contrat}` : '',
                                                form.rc_zone
                                            ].filter(Boolean).join(' - ') || '—' }}
                                        </span>
                                    </div>
                                </transition>
                            </div>
                        </div>

                        <!-- Capital social (toujours affiché, optionnel) -->
                        <div>
                            <label class="flex items-center gap-2 text-sm font-semibold text-stone-700 mb-1">
                                Capital social
                                <span class="text-[10px] font-normal text-stone-400 normal-case tracking-normal">optionnel — laisser 0 si non applicable</span>
                            </label>
                            <p class="text-xs text-stone-400 mb-2 leading-relaxed">Pour les SARL, SASU, EURL… Ignorez si auto-entrepreneur ou EI.</p>
                            <div class="flex items-center gap-2">
                                <input v-model.number="form.capital_social" type="number" min="0" step="100"
                                    placeholder="0"
                                    class="w-40 border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-stone-800 bg-stone-50 outline-none transition-all focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-500/10 font-mono"
                                    style="border-width:1.5px;" />
                                <span class="text-sm text-stone-400">€</span>
                                <span v-if="form.capital_social > 0" class="text-xs text-stone-400 ml-1">
                                    → au capital de {{ Number(form.capital_social).toLocaleString('fr-FR') }} €
                                </span>
                            </div>
                        </div>

                        <div v-if="!$account.account.societe.signature_entreprise">
                            <label class="flex items-center gap-2 text-sm font-semibold text-stone-700 mb-1">
                                Signature de l'entreprise
                                <span class="text-[10px] font-semibold uppercase tracking-wider text-orange-500 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">Documents</span>
                            </label>
                            <p class="text-xs text-stone-400 mb-2 leading-relaxed">Apposée automatiquement sur vos contrats et documents officiels.</p>

                            <div v-if="!form.signature_entreprise"
                                @click="$modal.show('modal-signature-bienvenue')"
                                class="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-stone-200 rounded-xl p-8 cursor-pointer bg-stone-50 hover:border-orange-400 hover:bg-orange-50/30 transition-all duration-200">
                                <i class="fas fa-signature text-2xl text-stone-300"></i>
                                <span class="text-sm text-stone-400">Cliquer pour ajouter la signature</span>
                            </div>

                            <div v-else class="flex items-center gap-4 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3">
                                <img :src="form.signature_entreprise" class="h-14 object-contain flex-1" alt="Signature" />
                                <button @click="$modal.show('modal-signature-bienvenue')"
                                    class="text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 whitespace-nowrap hover:bg-orange-100 transition-colors cursor-pointer border-none">
                                    <i class="fas fa-pencil-alt mr-1"></i> Modifier
                                </button>
                            </div>
                        </div>

                        <div v-if="missingFields.length === 0" class="text-center py-8 text-stone-400 text-sm">
                            <div class="text-center flex justify-center">
                                <i class="fas fa-check-circle text-3xl text-emerald-500 block mb-3"></i>
                            </div>
                            Toutes vos informations sont déjà complètes.
                        </div>
                    </div>

                    <div class="flex gap-3 mt-8 justify-end">
                        <button @click="step = 0"
                            class="flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-600 font-semibold py-2.5 px-5 rounded-xl transition-colors text-sm cursor-pointer border-none">
                            <i class="fas fa-arrow-left"></i> Retour
                        </button>
                        <button @click="end" :disabled="loading || !isFormValid"
                            class="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-60 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm cursor-pointer border-none"
                            style="box-shadow:0 4px 15px rgba(234,88,12,.3);">
                            <template v-if="!loading">
                                <i class="fas fa-check"></i> Terminer l'inscription
                            </template>
                            <template v-else>
                                <svg class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                Enregistrement…
                            </template>
                        </button>
                    </div>
                </div>
            </div>

            <!-- STEP 2 : Succès + Confetti -->
            <div v-else-if="step === 2" key="s2"
                class="fixed inset-0 flex items-center justify-center z-50 px-4"
                style="background:linear-gradient(135deg,#fffbf7 0%,#fff4ee 50%,#fef3c7 100%);">

                <canvas ref="confettiCanvas" class="fixed inset-0 pointer-events-none" style="z-index:103;"></canvas>

                <div class="relative text-center w-full max-w-md px-10 py-12 rounded-3xl border border-white/90"
                    style="z-index:102;background:rgba(255,255,255,0.88);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);box-shadow:0 8px 32px rgba(234,88,12,.12),0 2px 8px rgba(0,0,0,.06);animation:tf-pop-in .5s cubic-bezier(.34,1.56,.64,1) both;">

                    <div class="w-20 h-20 mx-auto mb-6">
                        <svg viewBox="0 0 52 52" class="w-full h-full">
                            <circle cx="26" cy="26" r="25" fill="none" stroke="#10b981" stroke-width="2"
                                style="stroke-dasharray:166;stroke-dashoffset:166;stroke-linecap:round;animation:tf-draw-circle .6s ease forwards;" />
                            <path fill="none" stroke="#10b981" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" d="M14 27l8 8 16-16"
                                style="stroke-dasharray:48;stroke-dashoffset:48;animation:tf-draw-tick .4s ease .6s forwards;" />
                        </svg>
                    </div>

                    <h2 class="text-3xl text-stone-800 mb-3"
                        style="font-family:'DM Serif Display',serif;letter-spacing:-0.5px;animation:tf-fade-up .5s ease .3s both;">
                        Bienvenue sur Termoflow !
                    </h2>
                    <p class="text-sm text-stone-500 leading-loose mb-7"
                        style="animation:tf-fade-up .5s ease .45s both;">
                        Votre espace professionnel est configuré et prêt à l'emploi.<br/>Bonne continuation
                    </p>

                    <div class="h-1.5 bg-stone-100 rounded-full overflow-hidden mb-2">
                        <div class="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transition-[width] duration-100"
                            :style="{ width: redirectProgress + '%' }"></div>
                    </div>
                    <p class="text-xs text-stone-400 mb-6">
                        Redirection dans {{ redirectCountdown }} seconde{{ redirectCountdown > 1 ? 's' : '' }}…
                    </p>

                    <button @click="redirect"
                        class="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 text-base border-none cursor-pointer hover:-translate-y-0.5"
                        style="box-shadow:0 4px 15px rgba(234,88,12,.3);">
                        Accéder maintenant <i class="fas fa-arrow-right"></i>
                    </button>
                </div>
            </div>

        </transition>
    </div>
</template>

<script>

export default {
    name: "Welcome",

    data() {
        return {
            step: 0,
            loading: false,
            selectedSignatureMethod: 'electronique',
            form: {
                num_capacite: '',
                // Sub-champs RC → reconstruits en string avant envoi
                rc_organisme: '',   // ex: "MAAF" ou "MCE001"
                rc_contrat:   '',   // ex: "101053256M"
                rc_zone:      '',   // ex: "France Métropolitaine" (optionnel)
                // Capital social (optionnel, 0 = non renseigné)
                capital_social:      0,
                signature_entreprise: '',
            },
            redirectCountdown: 5,
            redirectProgress: 0,
            countdownTimer: null,
            confettiRAF: null,
            injected: [],
        };
    },

    computed: {
        missingFields() {
            const s = this.$account?.account?.societe || {};
            const f = [];
            if (!s.num_capacite)        f.push({ key: 'num_capacite',        label: 'Numéro de capacité (CERFA)' });
            if (!s.responsable_rc)       f.push({ key: 'responsable_rc',       label: 'Code Responsabilité Civile (RC)' });
            if (!s.signature_entreprise) f.push({ key: 'signature_entreprise', label: "Signature de l'entreprise" });
            return f;
        },
        isFormValid() {
            const s = this.$account?.account?.societe || {};
            if (!s.num_capacite        && !this.form.num_capacite)        return false;
            if (!s.responsable_rc      && (!this.form.rc_organisme || !this.form.rc_contrat)) return false;
            if (!s.signature_entreprise && !this.form.signature_entreprise) return false;
            return true;
        },
    },

    mounted() {
        this._inject('link', { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap' });
        this._inject('style', {}, `
            @keyframes orb-drift {
                from { transform:translate(0,0) scale(1); }
                to   { transform:translate(30px,40px) scale(1.08); }
            }
            @keyframes tf-pop-in {
                from { opacity:0; transform:scale(.8) translateY(24px); }
                to   { opacity:1; transform:scale(1) translateY(0); }
            }
            @keyframes tf-emoji-pop {
                from { opacity:0; transform:scale(0) rotate(-20deg); }
                to   { opacity:1; transform:scale(1) rotate(0deg); }
            }
            @keyframes tf-draw-circle { to { stroke-dashoffset:0; } }
            @keyframes tf-draw-tick   { to { stroke-dashoffset:0; } }
            @keyframes tf-fade-up {
                from { opacity:0; transform:translateY(10px); }
                to   { opacity:1; transform:translateY(0); }
            }
            .tf-slide-enter-active, .tf-slide-leave-active {
                transition: opacity .3s ease, transform .3s cubic-bezier(.4,0,.2,1);
            }
            .tf-slide-enter      { opacity:0; transform:translateY(14px); }
            .tf-slide-leave-to   { opacity:0; transform:translateY(-10px); }
        `);
    },

    beforeDestroy() {
        this._stopConfetti();
        clearInterval(this.countdownTimer);
        this.injected.forEach(el => el.parentNode?.removeChild(el));
    },

    methods: {
        // ── API ──────────────────────────────────────────────────────────────
        async end() {
            if (!this.isFormValid) {
                this.$toast.error('Veuillez remplir tous les champs requis.');
                return;
            }
            this.loading = true;
            try {
                // Reconstruction responsable_rc
                const rcParts = [];
                if (this.form.rc_organisme) rcParts.push(this.form.rc_organisme.trim());
                if (this.form.rc_contrat)   rcParts.push(`Contrat n°${this.form.rc_contrat.trim()}`);
                if (this.form.rc_zone)      rcParts.push(this.form.rc_zone.trim());
                const responsable_rc = rcParts.join(' - ');

                const response = await this.$request.post('/societe/update-welcome', {
                    num_capacite:         this.form.num_capacite,
                    responsable_rc,
                    capital_social:       this.form.capital_social || 0,
                    signature_entreprise: this.form.signature_entreprise,
                });
                if (response == 1) {
                    this.step = 2;
                    this.$nextTick(() => { this._startConfetti(); this._startCountdown(); });
                }
            } finally {
                this.loading = false;
            }
        },

        redirect() {
            clearInterval(this.countdownTimer);
            this._stopConfetti();
            window.location.href = '/app';
        },

        logout() {
            localStorage.removeItem('auth-token');
            window.location.href = '/';
        },

        _startConfetti() {
            const canvas = this.$refs.confettiCanvas;
            if (!canvas) return;

            const W = window.innerWidth;
            const H = window.innerHeight;

            // Dimensions pixel du buffer de dessin
            canvas.width  = W;
            canvas.height = H;
            // Dimensions CSS explicites (nécessaire sur les éléments fixed)
            canvas.style.width  = W + 'px';
            canvas.style.height = H + 'px';

            const ctx    = canvas.getContext('2d');
            const colors = ['#f97316','#ea580c','#fbbf24','#34d399','#60a5fa','#a78bfa','#f472b6','#fff'];
            const pieces = Array.from({ length: 150 }, () => ({
                x:     Math.random() * W,
                y:     Math.random() * H - H,
                w:     Math.random() * 10 + 5,
                h:     Math.random() * 5 + 3,
                color: colors[Math.floor(Math.random() * colors.length)],
                rot:   Math.random() * Math.PI * 2,
                vx:    (Math.random() - 0.5) * 3,
                vy:    Math.random() * 4 + 2,
                vr:    (Math.random() - 0.5) * 0.15,
                alpha: 1,
            }));

            let frame = 0;
            const tick = () => {
                ctx.clearRect(0, 0, W, H);
                let alive = false;
                for (const p of pieces) {
                    p.x   += p.vx;
                    p.y   += p.vy;
                    p.rot += p.vr;
                    p.vy  += 0.08;
                    if (frame > 120) p.alpha -= 0.010;
                    if (p.alpha <= 0) continue;
                    alive = true;
                    ctx.save();
                    ctx.globalAlpha = p.alpha;
                    ctx.translate(p.x, p.y);
                    ctx.rotate(p.rot);
                    ctx.fillStyle = p.color;
                    ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                    ctx.restore();
                }
                frame++;
                if (alive && this.step === 2) {
                    this.confettiRAF = requestAnimationFrame(tick);
                } else {
                    ctx.clearRect(0, 0, W, H);
                }
            };
            requestAnimationFrame(tick);
        },

        _stopConfetti() {
            if (this.confettiRAF) cancelAnimationFrame(this.confettiRAF);
        },

        // ── Countdown ────────────────────────────────────────────────────────
        _startCountdown() {
            const TOTAL = 5000, TICK = 100;
            let elapsed = 0;
            this.countdownTimer = setInterval(() => {
                elapsed += TICK;
                this.redirectProgress  = Math.min(100, (elapsed / TOTAL) * 100);
                this.redirectCountdown = Math.max(0, Math.ceil((TOTAL - elapsed) / 1000));
                if (elapsed >= TOTAL) { clearInterval(this.countdownTimer); this.redirect(); }
            }, TICK);
        },

        // ── DOM helpers ──────────────────────────────────────────────────────
        _inject(tag, attrs, text = '') {
            const el = document.createElement(tag);
            Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            if (text) el.textContent = text;
            document.head.appendChild(el);
            this.injected.push(el);
        },
    },
};
</script>